import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, BarChart, Bar, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Users, UserPlus, TrendingUp, Swords, Target } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { usePrivateRooms } from '@/hooks/usePrivateRooms';
import { usePlayerProfiles } from '@/hooks/usePlayerProfiles';
import { AvatarDisplay } from '@/components/AvatarDisplay';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type GameSession = Database['public']['Tables']['game_sessions']['Row'];
type UserProfile = Database['public']['Tables']['user_profiles']['Row'];

interface PlayerComparisonPanelProps {
  userId: string;
  currentElo: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  gamesDrawn: number;
}

interface ComparisonStats {
  profile: UserProfile;
  games: GameSession[];
  eloHistory: Array<{ date: string; elo: number }>;
}

interface HeadToHeadRecord {
  totalGames: number;
  player1Wins: number;
  player2Wins: number;
  draws: number;
  games: GameSession[];
}

export function PlayerComparisonPanel({
  userId,
  currentElo,
  gamesPlayed,
  gamesWon,
  gamesLost,
  gamesDrawn
}: PlayerComparisonPanelProps) {
  const navigate = useNavigate();
  const { friends } = usePrivateRooms();
  
  const [comparisonMode, setComparisonMode] = useState<'friend' | 'elo-range'>('friend');
  const [selectedFriendId, setSelectedFriendId] = useState<string | null>(null);
  const [eloRange, setEloRange] = useState<50 | 100 | 200>(100);
  const [opponentStats, setOpponentStats] = useState<ComparisonStats | null>(null);
  const [headToHead, setHeadToHead] = useState<HeadToHeadRecord | null>(null);
  const [loading, setLoading] = useState(false);
  
  const acceptedFriends = useMemo(
    () => friends.filter(f => f.status === 'accepted'),
    [friends]
  );

  // Get friend IDs for profile fetching
  const friendIds = useMemo(
    () => acceptedFriends.map(f => 
      f.requester_id === userId ? f.addressee_id : f.requester_id
    ),
    [acceptedFriends, userId]
  );

  const { profiles: friendProfiles } = usePlayerProfiles(friendIds);

  // Fetch opponent stats
  const fetchPlayerStats = async (playerId: string): Promise<ComparisonStats | null> => {
    try {
      // Get profile
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', playerId)
        .single();

      if (profileError || !profile) return null;

      // Get game history
      const { data: games, error: gamesError } = await supabase
        .from('game_sessions')
        .select('*')
        .or(`white_player_id.eq.${playerId},black_player_id.eq.${playerId}`)
        .order('created_at', { ascending: false })
        .limit(200);

      if (gamesError) return null;

      // Get ELO history
      const { data: eloLogs, error: eloError } = await supabase
        .from('elo_change_logs')
        .select('created_at, white_player_id, black_player_id, white_elo_after, black_elo_after')
        .or(`white_player_id.eq.${playerId},black_player_id.eq.${playerId}`)
        .order('created_at', { ascending: true });

      const eloHistory = eloLogs?.map(log => ({
        date: log.created_at,
        elo: log.white_player_id === playerId ? log.white_elo_after : log.black_elo_after
      })) || [];

      return { profile, games: games || [], eloHistory };
    } catch (err) {
      console.error('Error fetching player stats:', err);
      return null;
    }
  };

  // Fetch head-to-head record
  const fetchHeadToHead = async (player1Id: string, player2Id: string): Promise<HeadToHeadRecord> => {
    const { data: games } = await supabase
      .from('game_sessions')
      .select('*')
      .or(
        `and(white_player_id.eq.${player1Id},black_player_id.eq.${player2Id}),` +
        `and(white_player_id.eq.${player2Id},black_player_id.eq.${player1Id})`
      );

    let player1Wins = 0, player2Wins = 0, draws = 0;
    
    games?.forEach(game => {
      if (game.winner === 'draw') {
        draws++;
      } else {
        const player1Color = game.white_player_id === player1Id ? 'white' : 'black';
        if (game.winner === player1Color) player1Wins++;
        else player2Wins++;
      }
    });

    return { 
      totalGames: games?.length || 0,
      player1Wins, 
      player2Wins, 
      draws,
      games: games || []
    };
  };

  // Fetch ELO range stats
  const fetchEloRangeStats = async (centerElo: number, range: number): Promise<ComparisonStats | null> => {
    const minElo = centerElo - range;
    const maxElo = centerElo + range;

    const { data: players } = await supabase
      .from('user_profiles')
      .select('*')
      .gte('elo_rating', minElo)
      .lte('elo_rating', maxElo)
      .neq('id', userId)
      .gte('games_played', 10)
      .limit(50);

    if (!players || players.length === 0) return null;

    // Calculate aggregate stats
    const totalGames = players.reduce((sum, p) => sum + p.games_played, 0);
    const totalWins = players.reduce((sum, p) => sum + p.games_won, 0);
    const totalLosses = players.reduce((sum, p) => sum + p.games_lost, 0);
    const totalDraws = players.reduce((sum, p) => sum + p.games_drawn, 0);
    const avgElo = Math.round(players.reduce((sum, p) => sum + p.elo_rating, 0) / players.length);

    const aggregateProfile: UserProfile = {
      id: 'elo-range',
      username: `Players ${centerElo}±${range}`,
      display_name: `ELO Range Average`,
      elo_rating: avgElo,
      games_played: Math.round(totalGames / players.length),
      games_won: Math.round(totalWins / players.length),
      games_lost: Math.round(totalLosses / players.length),
      games_drawn: Math.round(totalDraws / players.length),
      avatar_type: 'identicon',
      avatar_config: {},
      avatar_url: null,
      first_name: null,
      last_name: null,
      full_name: null,
      preferred_time_control: null,
      location_consent_updated_at: null,
      location_sharing_consent: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    return { profile: aggregateProfile, games: [], eloHistory: [] };
  };

  // Load comparison data
  useEffect(() => {
    const loadComparison = async () => {
      if (comparisonMode === 'friend' && selectedFriendId) {
        setLoading(true);
        const stats = await fetchPlayerStats(selectedFriendId);
        setOpponentStats(stats);
        
        if (stats) {
          const h2h = await fetchHeadToHead(userId, selectedFriendId);
          setHeadToHead(h2h);
        }
        
        setLoading(false);
      } else if (comparisonMode === 'elo-range') {
        setLoading(true);
        const stats = await fetchEloRangeStats(currentElo, eloRange);
        setOpponentStats(stats);
        setHeadToHead(null);
        setLoading(false);
      }
    };

    loadComparison();
  }, [comparisonMode, selectedFriendId, eloRange, userId, currentElo]);

  // Calculate comparison metrics
  const comparisonMetrics = useMemo(() => {
    if (!opponentStats) return null;

    const userWinRate = gamesPlayed > 0 ? (gamesWon / gamesPlayed) * 100 : 0;
    const opponentWinRate = opponentStats.profile.games_played > 0 
      ? (opponentStats.profile.games_won / opponentStats.profile.games_played) * 100 
      : 0;

    return {
      winRate: {
        user: userWinRate.toFixed(1),
        opponent: opponentWinRate.toFixed(1),
        better: userWinRate >= opponentWinRate ? 'user' : 'opponent'
      },
      gamesPlayed: {
        user: gamesPlayed,
        opponent: opponentStats.profile.games_played,
        better: gamesPlayed >= opponentStats.profile.games_played ? 'user' : 'opponent'
      },
      totalWins: {
        user: gamesWon,
        opponent: opponentStats.profile.games_won,
        better: gamesWon >= opponentStats.profile.games_won ? 'user' : 'opponent'
      },
      eloDifference: currentElo - opponentStats.profile.elo_rating
    };
  }, [opponentStats, gamesPlayed, gamesWon, currentElo]);

  // Calculate time control comparison
  const timeControlComparison = useMemo(() => {
    if (!opponentStats) return [];

    // Get user's games
    const userGames = opponentStats.games; // We'll need to fetch user's games separately
    const tcMap = new Map<string, { user: number; opponent: number }>();

    // This is simplified - in a real implementation, we'd fetch both players' games
    return [];
  }, [opponentStats]);

  if (acceptedFriends.length === 0 && comparisonMode === 'friend') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Compare Statistics</CardTitle>
          <CardDescription>Compare your stats with friends or players in your ELO range</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No Friends Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add friends to compare your stats with them
            </p>
            <Button onClick={() => navigate('/community')}>
              <UserPlus className="w-4 h-4 mr-2" />
              Find Friends
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle>Compare Statistics</CardTitle>
            <CardDescription>Compare your stats with friends or players in your ELO range</CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={comparisonMode} onValueChange={(v) => setComparisonMode(v as 'friend' | 'elo-range')}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Compare with..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="elo-range">ELO Range</SelectItem>
              </SelectContent>
            </Select>

            {comparisonMode === 'friend' ? (
              <Select value={selectedFriendId || ''} onValueChange={setSelectedFriendId}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Select friend..." />
                </SelectTrigger>
                <SelectContent>
                  {acceptedFriends.map(friend => {
                    const friendId = friend.requester_id === userId ? friend.addressee_id : friend.requester_id;
                    const friendProfile = friendProfiles[friendId];
                    
                    return (
                      <SelectItem key={friendId} value={friendId}>
                        {friendProfile?.username || friendProfile?.display_name || `Friend (${friendId.substring(0, 8)}...)`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <Select value={String(eloRange)} onValueChange={(v) => setEloRange(Number(v) as 50 | 100 | 200)}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Select range..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">±50 ELO</SelectItem>
                  <SelectItem value="100">±100 ELO</SelectItem>
                  <SelectItem value="200">±200 ELO</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="py-12">
            <Skeleton className="h-[400px] w-full" />
          </div>
        ) : !opponentStats ? (
          <div className="text-center py-12 text-muted-foreground">
            <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Select a player to compare stats</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Player Headers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="text-center p-4 bg-primary/5">
                <AvatarDisplay
                  username="You"
                  avatarConfig={null}
                  avatarUrl={null}
                  size="lg"
                  className="mx-auto mb-2"
                />
                <h3 className="font-bold">You</h3>
                <Badge variant="default" className="mt-2">{currentElo} ELO</Badge>
              </Card>

              <Card className="text-center p-4 bg-muted/30">
                <AvatarDisplay
                  username={opponentStats.profile.username || ''}
                  avatarConfig={opponentStats.profile.avatar_config as any}
                  avatarUrl={opponentStats.profile.avatar_url}
                  size="lg"
                  className="mx-auto mb-2"
                />
                <h3 className="font-bold">
                  {opponentStats.profile.username || opponentStats.profile.display_name}
                </h3>
                <Badge variant="secondary" className="mt-2">
                  {opponentStats.profile.elo_rating} ELO
                </Badge>
              </Card>
            </div>

            {/* Quick Stats Comparison */}
            {comparisonMetrics && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-3">
                  <p className="text-xs text-muted-foreground mb-2">Win Rate</p>
                  <div className="flex justify-between items-center">
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.winRate.better === 'user' && "text-primary"
                    )}>
                      {comparisonMetrics.winRate.user}%
                    </span>
                    <span className="text-xs text-muted-foreground">vs</span>
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.winRate.better === 'opponent' && "text-chart-2"
                    )}>
                      {comparisonMetrics.winRate.opponent}%
                    </span>
                  </div>
                  {comparisonMetrics.winRate.better === 'user' && (
                    <TrendingUp className="w-3 h-3 text-primary mx-auto mt-1" />
                  )}
                </Card>

                <Card className="p-3">
                  <p className="text-xs text-muted-foreground mb-2">Games Played</p>
                  <div className="flex justify-between items-center">
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.gamesPlayed.better === 'user' && "text-primary"
                    )}>
                      {comparisonMetrics.gamesPlayed.user}
                    </span>
                    <span className="text-xs text-muted-foreground">vs</span>
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.gamesPlayed.better === 'opponent' && "text-chart-2"
                    )}>
                      {comparisonMetrics.gamesPlayed.opponent}
                    </span>
                  </div>
                </Card>

                <Card className="p-3">
                  <p className="text-xs text-muted-foreground mb-2">Total Wins</p>
                  <div className="flex justify-between items-center">
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.totalWins.better === 'user' && "text-primary"
                    )}>
                      {comparisonMetrics.totalWins.user}
                    </span>
                    <span className="text-xs text-muted-foreground">vs</span>
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.totalWins.better === 'opponent' && "text-chart-2"
                    )}>
                      {comparisonMetrics.totalWins.opponent}
                    </span>
                  </div>
                </Card>

                <Card className="p-3">
                  <p className="text-xs text-muted-foreground mb-2">ELO Difference</p>
                  <div className="text-center">
                    <span className={cn(
                      "font-bold text-sm",
                      comparisonMetrics.eloDifference > 0 ? "text-primary" : "text-chart-2"
                    )}>
                      {comparisonMetrics.eloDifference > 0 ? '+' : ''}{comparisonMetrics.eloDifference}
                    </span>
                  </div>
                </Card>
              </div>
            )}

            {/* Head-to-Head Record */}
            {comparisonMode === 'friend' && headToHead && headToHead.totalGames > 0 && (
              <Card className="p-4 bg-accent/5">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Swords className="w-5 h-5" />
                  Head-to-Head Record
                </h3>
                <div className="flex items-center justify-around">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">
                      {headToHead.player1Wins}
                    </div>
                    <p className="text-sm text-muted-foreground">Your Wins</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-muted-foreground">
                      {headToHead.draws}
                    </div>
                    <p className="text-xs text-muted-foreground">Draws</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-bold text-chart-2">
                      {headToHead.player2Wins}
                    </div>
                    <p className="text-sm text-muted-foreground">Their Wins</p>
                  </div>
                </div>
                
                <Progress 
                  value={headToHead.totalGames > 0 ? (headToHead.player1Wins / headToHead.totalGames) * 100 : 0} 
                  className="mt-4"
                />
                <p className="text-center text-xs text-muted-foreground mt-2">
                  {headToHead.totalGames} games played together
                </p>
              </Card>
            )}

            {/* ELO Progression Comparison */}
            {opponentStats.eloHistory.length > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-3">ELO Rating Comparison</h3>
                <ChartContainer
                  config={{
                    userElo: { label: "Your ELO", color: "hsl(var(--primary))" },
                    opponentElo: { label: "Their ELO", color: "hsl(var(--chart-2))" }
                  }}
                  className="h-[250px]"
                >
                  <LineChart data={opponentStats.eloHistory.slice(-20)}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(v) => format(new Date(v), 'MMM dd')}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line 
                      type="monotone" 
                      dataKey="elo" 
                      stroke="var(--color-opponentElo)" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ChartContainer>
              </Card>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
